/**
 * Recovery for a page that has gone unclickable.
 *
 * Radix's DismissableLayer parks `pointer-events: none` on <body> while a modal
 * layer (Dialog, DropdownMenu, Select, modal Popover) is open, and restores it
 * when the last one closes. Any missed restore strands the style, and the whole
 * app goes dead: nothing is clickable and hover cursors stop changing, while
 * scrolling keeps working because it is driven off the compositor. That is the
 * shape reported in MSMS-53, and individual modals had been clearing the style
 * by hand on close.
 *
 * `patches/@radix-ui+react-dismissable-layer+1.1.11.patch` closes the ways the
 * restore is missed. This is the safety net under it, covering every layer at
 * once: a stuck page heals itself instead of needing a reload.
 */

/**
 * Content rendered by the Radix layers that legitimately disable outside
 * pointer events. While any of these is in the document, `none` on <body> is
 * correct and has to be left alone.
 */
const OPEN_LAYER_SELECTOR = [
  // Dropdown, select, popover and tooltip content is portalled into this wrapper
  "[data-radix-popper-content-wrapper]",
  // Menus rendered without the popper wrapper (context menus, sub-menus)
  "[data-radix-menu-content]",
  // Dialogs, which carry no data-radix attribute of their own
  '[role="dialog"][data-state="open"]',
].join(",");

/**
 * Clears a `pointer-events: none` on <body> that no open layer accounts for.
 *
 * @returns Whether a stranded style was found and cleared.
 */
export function releaseStuckBodyPointerEvents(): boolean {
  // Radix writes this as an inline style, so reading the inline style is both
  // exact and free — no computed-style lookup on what is a hot event path.
  if (document.body.style.pointerEvents !== "none") return false;
  if (document.querySelector(OPEN_LAYER_SELECTOR)) return false;

  document.body.style.pointerEvents = "";
  return true;
}

/**
 * Watches for the stuck state and clears it.
 *
 * Pointer movement is the earliest signal available: it arrives while the user
 * is still only hovering, so the page is live again before the click that would
 * otherwise be swallowed. `pointerdown` covers touch, which never hovers. Both
 * still reach the document while <body> is disabled, because `pointer-events`
 * is inherited and <html> is <body>'s parent, not its descendant.
 *
 * @param onRecover - Called the first time a stranded style is cleared.
 * @returns A teardown that removes the listeners.
 */
export function installBodyPointerEventsGuard(
  onRecover?: () => void
): () => void {
  let recovered = false;

  const check = () => {
    if (!releaseStuckBodyPointerEvents()) return;
    // Report once per page load: a page that gets stuck repeatedly would
    // otherwise bury the signal under duplicates.
    if (recovered) return;
    recovered = true;
    onRecover?.();
  };

  document.addEventListener("pointermove", check, true);
  document.addEventListener("pointerdown", check, true);

  return () => {
    document.removeEventListener("pointermove", check, true);
    document.removeEventListener("pointerdown", check, true);
  };
}